import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LogIn, LogOut, History, AlertTriangle, LoaderCircle, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { api, fmtKLTime, fmtKLDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

export default function StaffHome() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const { data: status, isLoading } = useQuery({
    queryKey: ["my-status"],
    queryFn: async () => (await api.get("/punches/me/status")).data,
    refetchOnMount: "always",
  });

  const clockedIn = status?.clockedIn;
  const nextType = clockedIn ? "clock_out" : "clock_in";

  return (
    <AppShell>
      <div className="mx-auto flex max-w-md flex-col gap-6 pb-32">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {fmtKLDate(new Date().toISOString())}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">{user?.fullName}</span>
              <span
                data-testid="staff-home-clock-status"
                className={`rounded-full px-3 py-1 text-sm font-semibold ${
                  clockedIn ? "bg-emerald-100 text-emerald-800" : "bg-secondary text-muted-foreground"
                }`}
              >
                {isLoading ? "…" : clockedIn ? t("clockedIn") : t("clockedOut")}
              </span>
            </div>
            {status?.lastPunch && (
              <p className="text-sm text-muted-foreground">
                {t("lastPunch")}:{" "}
                <span className="font-mono tabular-nums">{fmtKLTime(status.lastPunch.serverTimestamp)}</span>{" "}
                ({status.lastPunch.punchType === "clock_in" ? t("clockInLabel") : t("clockOutLabel")})
              </p>
            )}
            {status && !status.enrolled && (
              <p data-testid="not-enrolled-warning" className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t("notEnrolled")}
              </p>
            )}
          </CardContent>
        </Card>

        {status?.todayPunches?.length > 0 && (
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t("today")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {status.todayPunches.map((p) => (
                <div key={p.id} data-testid="today-punch-row" className="flex items-center justify-between gap-2 border-b border-border pb-2 last:border-0 last:pb-0">
                  <div className="flex items-center gap-2">
                    {p.punchType === "clock_in"
                      ? <LogIn className="h-4 w-4 text-emerald-700" />
                      : <LogOut className="h-4 w-4 text-sky-700" />}
                    <span className="font-mono text-sm tabular-nums">{fmtKLTime(p.serverTimestamp)}</span>
                  </div>
                  <StatusBadge kind="review" value={p.reviewStatus} />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate("/staff/history")}
            data-testid="view-history-button"
            className="h-11 justify-center"
          >
            <History className="mr-2 h-4 w-4" /> {t("myHistory")}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/staff/summary")}
            data-testid="view-summary-button"
            className="h-11 justify-center"
          >
            <BarChart3 className="mr-2 h-4 w-4" /> {t("mySummary")}
          </Button>
        </div>
      </div>

      {/* one huge thumb-zone punch button (spec §7) */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card/95 p-4 backdrop-blur-sm rk-safe-bottom">
        <div className="mx-auto max-w-md">
          <Button
            data-testid="punch-primary-button"
            disabled={isLoading}
            onClick={() => navigate(`/staff/capture/${nextType}`)}
            className={`h-16 w-full text-lg font-semibold active:scale-[0.99] ${
              clockedIn ? "bg-sky-700 hover:bg-sky-700/90" : ""
            }`}
          >
            {isLoading ? (
              <LoaderCircle className="h-5 w-5 animate-spin" />
            ) : clockedIn ? (
              <><LogOut className="mr-2 h-5 w-5" /> {t("clockOut")}</>
            ) : (
              <><LogIn className="mr-2 h-5 w-5" /> {t("clockIn")}</>
            )}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
